import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, Plus, EllipsisVertical, SquarePen, Trash2, BookOpen, ChevronUp, ChevronDown, StickyNote, AlertTriangle } from 'lucide-react';
import type { Engagement, Entity, JournalEntryHeader, JESortField } from '../types';
import {
  getJournalEntryHeaders,
  getAllJournalEntryLines,
  getAccounts,
  getEntities,
  deleteJournalEntryHeader,
  updateSignoffState,
  acknowledgePrincipalReview,
} from '../db/database';
import { displayAmountRounded } from '../utils/format';
import {
  isTierSigned,
  isTierClickable,
  computeNextSignoffState,
  getSignoffTitle,
  hasCautionFlag,
} from '../utils/signoff';
import type { SignoffTier } from '../utils/signoff';
import { Button } from './Button';
import { SignoffCheckbox } from './SignoffCheckbox';
import JournalEntryModal from './JournalEntryModal';
import JournalEntryEditor from './JournalEntryEditor';

// ============================================================================
// TypeBadge
// ============================================================================

function TypeBadge({ isRJE }: { isRJE: boolean }) {
  return isRJE ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sage-100 text-sage-700 dark:bg-sage-900 dark:text-sage-300">
      RJE
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
      AJE
    </span>
  );
}

// ============================================================================
// RowKebabMenu
// ============================================================================

interface RowKebabMenuProps {
  onEditMetadata: () => void;
  onDelete: () => void;
}

function RowKebabMenu({ onEditMetadata, onDelete }: RowKebabMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpwards(window.innerHeight - rect.bottom < 150);
    }
    setIsOpen((v) => !v);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded transition-colors"
        title="More actions"
      >
        <EllipsisVertical className="w-5 h-5 text-slate-500 dark:text-slate-400" />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 w-44 glass-panel rounded-lg py-1 z-50 ${
            openUpwards ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onEditMetadata(); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-slate-800 dark:text-slate-100"
          >
            <SquarePen className="w-4 h-4" />
            Edit Metadata
          </button>
          <div className="border-t border-slate-200 dark:border-neutral-700 my-1" />
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SortIndicator
// ============================================================================

function SortIndicator({ field, current, direction }: {
  field: JESortField;
  current: JESortField;
  direction: 'asc' | 'desc';
}) {
  if (field !== current) return null;
  return direction === 'asc'
    ? <ChevronUp className="inline w-3 h-3 ml-1" />
    : <ChevronDown className="inline w-3 h-3 ml-1" />;
}

// ============================================================================
// JournalEntryList
// ============================================================================

interface JournalEntryListProps {
  engagement: Engagement;
  searchHint?: string;
  initialHeader?: JournalEntryHeader | null;
  onInitialHeaderConsumed?: () => void;
}

export default function JournalEntryList({ engagement, searchHint, initialHeader, onInitialHeaderConsumed }: JournalEntryListProps) {
  const [headers, setHeaders] = useState<JournalEntryHeader[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<JESortField>('jeNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [deleteTarget, setDeleteTarget] = useState<JournalEntryHeader | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<JournalEntryHeader | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState<JournalEntryHeader | null>(null);
  const [accountFilter, setAccountFilter] = useState('');
  const [headerAccountsMap, setHeaderAccountsMap] = useState<Map<string, Set<string>>>(new Map());

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadHeaders = useCallback(async () => {
    if (!engagement.id) return;
    setIsLoading(true);

    // Step 1: entities + headers first — display immediately
    const entityList = await getEntities(engagement.id).catch(() => []);
    setEntities(entityList);

    let data: JournalEntryHeader[];
    try {
      data = await getJournalEntryHeaders(engagement.id);
      if (data.length > 0) {
        setHeaders(data.filter(h => h.netAdjustmentCents === undefined));
      }
    } catch (err) {
      console.error('Failed to load JournalEntryHeaders:', err);
      return;
    } finally {
      setIsLoading(false);
    }

    // Step 2: lines + accounts for account-number filter (non-critical)
    try {
      const [allLines, allAccounts] = await Promise.all([
        getAllJournalEntryLines(engagement.id),
        getAccounts(engagement.id),
      ]);

      // Build accountId → accountNumber lookup
      const accNumberById = new Map(allAccounts.map(a => [a.id!, a.accountNumber]));

      // Build headerId → Set<accountNumber>
      const accountsMap = new Map<string, Set<string>>();
      for (const line of allLines) {
        const accNum = accNumberById.get(line.accountId);
        if (!accNum) continue;
        if (!accountsMap.has(line.headerId)) accountsMap.set(line.headerId, new Set());
        accountsMap.get(line.headerId)!.add(accNum);
      }

      setHeaderAccountsMap(accountsMap);
    } catch (err) {
      console.error('Failed to load lines/accounts for filtering (non-fatal):', err);
      // Headers still visible; account filter just won't be populated
    }
  }, [engagement.id]);

  useEffect(() => { loadHeaders(); }, [loadHeaders]);

  // Deep-link: if a specific header was passed in, open it immediately on mount
  useEffect(() => {
    if (initialHeader) setSelectedHeader(initialHeader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply searchHint once on mount as an account filter
  useEffect(() => {
    if (searchHint) {
      setAccountFilter(searchHint);
    }
  }, [searchHint]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────

  const displayedHeaders = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    let filtered = headers;

    // Account filter (from TB deep link) — match headers that have lines for this account
    const hasAccountFilter = typeof accountFilter === 'string' && accountFilter.trim() !== '';
    if (hasAccountFilter) {
      const searchString = accountFilter.trim().toLowerCase();
      filtered = filtered.filter(h =>
        headerAccountsMap.get(h.id!)?.has(searchString) ?? false
      );
    }

    // Text search by JE # or name
    if (q) {
      filtered = filtered.filter(
        (h) =>
          h.jeNumber.toLowerCase().includes(q) ||
          h.jeName.toLowerCase().includes(q)
      );
    }

    const SIGNOFF_ORDER: Record<string, number> = {
      null: 0, Prepared: 1, Principal: 2, Final: 3,
    };

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'jeNumber':
          cmp = a.jeNumber.localeCompare(b.jeNumber, undefined, { numeric: true });
          break;
        case 'jeName':
          cmp = a.jeName.localeCompare(b.jeName);
          break;
        case 'isRJE':
          cmp = Number(a.isRJE) - Number(b.isRJE);
          break;
        case 'totalDebits':
          cmp = a.totalDebits - b.totalDebits;
          break;
        case 'signoffState':
          cmp = (SIGNOFF_ORDER[String(a.signoffState)] ?? 0) -
                (SIGNOFF_ORDER[String(b.signoffState)] ?? 0);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [headers, searchQuery, sortField, sortDirection, accountFilter, headerAccountsMap]);

  // ── Sort handler ──────────────────────────────────────────────────────────

  const handleSort = (field: JESortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ── Signoff handlers ──────────────────────────────────────────────────────

  const handleSign = useCallback(async (header: JournalEntryHeader, tier: SignoffTier) => {
    const signed = isTierSigned(tier, header.signoffState);
    const cautionActive = hasCautionFlag(header);

    if (signed && tier === 'Principal' && cautionActive) {
      // Re-sign: update review timestamp without changing signoffState
      const ts = Date.now();
      setHeaders((prev) =>
        prev.map((h) => (h.id === header.id ? { ...h, lastPrincipalReviewAt: ts } : h))
      );
      try {
        await acknowledgePrincipalReview(header.id!);
      } catch (err) {
        console.error('Failed to re-sign:', err);
        setHeaders((prev) =>
          prev.map((h) => (h.id === header.id ? { ...h, lastPrincipalReviewAt: header.lastPrincipalReviewAt } : h))
        );
      }
      return;
    }
    if (signed) return; // already signed, no action on left-click

    const next = computeNextSignoffState(header.signoffState, tier);
    setHeaders((prev) =>
      prev.map((h) => (h.id === header.id ? { ...h, signoffState: next } : h))
    );
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to update signoff:', err);
      setHeaders((prev) =>
        prev.map((h) => (h.id === header.id ? { ...h, signoffState: header.signoffState } : h))
      );
    }
  }, []);

  const handleRemove = useCallback(async (header: JournalEntryHeader, tier: SignoffTier) => {
    if (!isTierClickable(tier, header.signoffState)) return;
    const next = computeNextSignoffState(header.signoffState, tier);
    setHeaders((prev) =>
      prev.map((h) => (h.id === header.id ? { ...h, signoffState: next } : h))
    );
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to remove signoff:', err);
      setHeaders((prev) =>
        prev.map((h) => (h.id === header.id ? { ...h, signoffState: header.signoffState } : h))
      );
    }
  }, []);

  // ── Delete handler ────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteJournalEntryHeader(deleteTarget.id);
      setDeleteTarget(null);
      await loadHeaders();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Editor navigation ─────────────────────────────────────────────────────

  if (selectedHeader) {
    return (
      <JournalEntryEditor
        header={selectedHeader}
        engagement={engagement}
        entities={entities}
        onBack={(reload) => {
          if (initialHeader && selectedHeader?.id === initialHeader.id) {
            onInitialHeaderConsumed?.();
          }
          setSelectedHeader(null);
          if (reload) loadHeaders();
        }}
      />
    );
  }

  // ── Shared th className ───────────────────────────────────────────────────

  const thBase =
    'px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 select-none transition-colors';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-taxtrail-bg dark:bg-neutral-900 transition-colors duration-300">

      {/* ── CONTROL BAR ──────────────────────────────────────────────────── */}
      <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-700 z-20 transition-colors duration-300">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-6 h-6 text-sage-500" />
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Journal Entries
            </h1>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {engagement.clientName}
          </p>
          <div className="flex gap-3 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by JE # or name…"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 text-slate-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm transition-colors duration-300"
              />
            </div>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => { setEditTarget(null); setIsModalOpen(true); }}
            >
              NEW JOURNAL ENTRY
            </Button>
          </div>
        </div>
      </div>

      {/* ── TABLE CONTAINER ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden p-6">
        <div
          className="h-full overflow-auto border border-gray-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-transparent transition-colors duration-300"
          style={{ scrollbarGutter: 'stable' }}
        >
          <table className="w-full border-collapse">

            {/* ── THEAD ──────────────────────────────────────────────────── */}
            <thead className="sticky top-0 bg-slate-100 dark:bg-neutral-800 border-b border-slate-200 dark:border-neutral-700 z-10">
              <tr>
                <th
                  onClick={() => handleSort('isRJE')}
                  className={`${thBase} text-left w-20`}
                >
                  Type<SortIndicator field="isRJE" current={sortField} direction={sortDirection} />
                </th>
                <th
                  onClick={() => handleSort('jeNumber')}
                  className={`${thBase} text-left w-32`}
                >
                  JE #<SortIndicator field="jeNumber" current={sortField} direction={sortDirection} />
                </th>
                <th
                  onClick={() => handleSort('totalDebits')}
                  className={`${thBase} text-right w-32`}
                >
                  Amount<SortIndicator field="totalDebits" current={sortField} direction={sortDirection} />
                </th>
                <th
                  onClick={() => handleSort('jeName')}
                  className={`${thBase} text-left`}
                >
                  Name<SortIndicator field="jeName" current={sortField} direction={sortDirection} />
                </th>
                <th className="px-2 py-3 w-10" />
                <th
                  onClick={() => handleSort('signoffState')}
                  className={`${thBase} text-center w-14`}
                  title="Sort by signoff status"
                >
                  Prep<SortIndicator field="signoffState" current={sortField} direction={sortDirection} />
                </th>
                <th className="px-2 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-center w-14">
                  Prin
                </th>
                <th className="px-2 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-center w-14">
                  Final
                </th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>

            {/* ── TBODY ──────────────────────────────────────────────────── */}
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-20 text-slate-500 dark:text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              ) : displayedHeaders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-20 text-slate-500 dark:text-slate-400">
                    {searchQuery ? (
                      <p className="text-sm">
                        No journal entries match &ldquo;{searchQuery}&rdquo;
                      </p>
                    ) : (
                      <div>
                        <BookOpen className="w-12 h-12 text-slate-200 dark:text-slate-800 mx-auto mb-3" />
                        <p className="text-sm font-medium">No journal entries yet.</p>
                        <p className="text-xs mt-1 text-slate-400 dark:text-slate-600">
                          Click &ldquo;New Journal Entry&rdquo; to get started.
                        </p>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                displayedHeaders.map((header) => {
                  const amt = displayAmountRounded(header.totalDebits);
                  return (
                    <tr
                      key={header.id}
                      onDoubleClick={() => setSelectedHeader(header)}
                      className="border-b border-slate-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-slate-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors"
                    >
                      {/* Type badge */}
                      <td className="px-4 py-3">
                        <TypeBadge isRJE={header.isRJE} />
                      </td>
                      {/* JE # */}
                      <td className="px-4 py-3 text-sm font-mono font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {header.jeNumber}
                      </td>
                      {/* Amount */}
                      <td
                        className={`px-4 py-3 text-sm font-mono text-right whitespace-nowrap ${
                          amt.isNegative
                            ? 'text-taxtrail-red'
                            : 'text-slate-800 dark:text-slate-100'
                        }`}
                      >
                        {amt.text}
                      </td>
                      {/* Name */}
                      <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
                        <span className="flex items-center gap-1.5">
                          {header.jeName}
                          {header.notes && (
                            <span title={header.notes} className="inline-flex">
                              <StickyNote className="w-3.5 h-3.5 shrink-0 text-amber-500 dark:text-amber-400" />
                            </span>
                          )}
                        </span>
                      </td>
                      {/* Caution flag */}
                      <td className="px-2 py-3 text-center">
                        {hasCautionFlag(header) && (
                          <span title="Edited after Principal review — left-click Principal to re-sign">
                            <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 inline" />
                          </span>
                        )}
                      </td>
                      {/* Prepared */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Prepared"
                          isSigned={isTierSigned('Prepared', header.signoffState)}
                          isClickable={isTierClickable('Prepared', header.signoffState)}
                          title={getSignoffTitle('Prepared', isTierSigned('Prepared', header.signoffState), isTierClickable('Prepared', header.signoffState), header.signoffState)}
                          onSign={() => handleSign(header, 'Prepared')}
                          onRemove={() => handleRemove(header, 'Prepared')}
                        />
                      </td>
                      {/* Principal */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Principal"
                          isSigned={isTierSigned('Principal', header.signoffState)}
                          isClickable={isTierClickable('Principal', header.signoffState)}
                          title={getSignoffTitle('Principal', isTierSigned('Principal', header.signoffState), isTierClickable('Principal', header.signoffState), header.signoffState, hasCautionFlag(header))}
                          hasCautionFlag={hasCautionFlag(header)}
                          onSign={() => handleSign(header, 'Principal')}
                          onRemove={() => handleRemove(header, 'Principal')}
                        />
                      </td>
                      {/* Final */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Final"
                          isSigned={isTierSigned('Final', header.signoffState)}
                          isClickable={isTierClickable('Final', header.signoffState)}
                          title={getSignoffTitle('Final', isTierSigned('Final', header.signoffState), isTierClickable('Final', header.signoffState), header.signoffState)}
                          onSign={() => handleSign(header, 'Final')}
                          onRemove={() => handleRemove(header, 'Final')}
                        />
                      </td>
                      {/* Kebab */}
                      <td className="px-2 py-3">
                        <RowKebabMenu
                          onEditMetadata={() => { setEditTarget(header); setIsModalOpen(true); }}
                          onDelete={() => setDeleteTarget(header)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── JOURNAL ENTRY MODAL ──────────────────────────────────────────── */}
      <JournalEntryModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditTarget(null); }}
        onSave={(newHeader) => {
          if (newHeader) {
            setSelectedHeader(newHeader);
          } else {
            loadHeaders();
          }
        }}
        engagement={engagement}
        entities={entities}
        headerToEdit={editTarget}
      />

      {/* ── DELETE CONFIRMATION MODAL ─────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
          <div className="glass-panel max-w-md w-full p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
              Delete Journal Entry?
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Deleting{' '}
              <span className="font-bold text-slate-900 dark:text-slate-100">
                {deleteTarget.jeNumber} — {deleteTarget.jeName}
              </span>{' '}
              will also delete all of its line items. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
